<template>
    <!-- Need to add height inherit because Vue 2 don't support multiple root ele -->
    <div style="height: inherit">
        <section class="chat-app-window position-relative">
            <start-chat-placeholder
                v-if="!formattedActiveChat"
                @update:mq-shall-show-left-sidebar="mqShallShowLeftSidebar = !mqShallShowLeftSidebar"
            />

            <div v-else class="active-chat">
                <div class="d-flex full-height">
                    <div class="flex-column d-flex position-relative" style="width: 250px; flex: 1">
                        <active-chat-navbar
                            :active-chat="formattedActiveChat"
                            :is-archive-chat-pending="isArchiveChatPending"
                            :is-assign-chat-pending="isAssignChatPending"
                            :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
                            @unarchive-chat="unarchiveChat"
                            @assign-chat="assignChat"
                            @toggle-mobile-plugin-bar="mobilePluginBarActive = !mobilePluginBarActive"
                        />

                        <div ref="scrollContainer" class="scroll-container">
                            <active-chat-log
                                :active-chat="formattedActiveChat"
                                :chat-entry-input="chatEntryInput"
                                :files="getFiles"
                                :is-loading-files="isLoadingFiles"
                                :is-send-pending="isSendPending"
                                @remove-file="removeFile"
                                @upload-files="localUploadFiles($event)"
                                @scroll-to-bottom="scrollToBottomInActiveChatLog"
                                @send-message="sendMessage"
                            />
                        </div>
                    </div>

                    <!--
@TODO make use of slots.
Please check https://gitlab.just-internet.nl/saysimple-messaging-module/frontend-v2/-/commit/90879931d15d8aedce8142379bf467eb46772b4f
-->
                    <active-chat-sidebars
                        :active-chat="formattedActiveChat"
                        :internal-comments="internalComments"
                        :mobile-plugin-bar-active="mobilePluginBarActive"
                        @set-chat-entry-input="chatEntryInput = $event"
                        @update-linked-tags="updateLinkedTags"
                        @create-internal-comment="createInternalComment"
                        @update-internal-comment="updateInternalComment"
                        @delete-internal-comment="deleteInternalComment"
                    />
                </div>
            </div>

            <b-overlay :show="isLoadingActiveChat" no-wrap></b-overlay>
        </section>

        <portal to="content-renderer-sidebar-left">
            <chat-left-sidebar
                :active-chat="formattedActiveChat"
                :active-filter="getActiveFilter"
                :delete-by-identity="false"
                :filters="filters"
                :formatted-chats-list="formattedChatsList"
                :inbox-module-type="InboxModuleType.EMAIL"
                :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
                :smart-inbox-available="smartInboxAvailable"
                @delete-chat="deleteChat"
                @apply-filter="applyFilter"
                @open-chat="openChat"
                @unarchive-chat="unarchiveChat"
                @assign-chat="assignChat"
            />
        </portal>
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import useAws from "@/aws/useAws";
import useActiveChat from "@/composables/useActiveChat";
import useActiveChatInternalComments from "@/composables/useActiveChatInternalComments";
import useActiveChatTemplates from "@/composables/useActiveChatTemplates"; // @NOTE old
import useChats from "@/composables/useChats";
import { useCookies } from "@/composables/useCookies";
import useEmailFilters from "@/composables/useEmailFilters";

// Composables
import useFileUploads from "@/composables/useFileUploads";
import useUsers from "@/composables/useUsers";
import useUserSettings from "@/composables/useUserSettings";
import useArchiveChat from "@/composables/v2.1/useArchiveChat";
import useChannels from "@/composables/v2.1/useChannels";
import useContacts from "@/composables/v2.1/useContacts";
import useDeleteChat from "@/composables/v2.1/useDeleteChat";
import useEmailStatusFilters from "@/composables/v2.1/useEmailStatusFilters";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useMultiSelect from "@/composables/v2.1/useMultiSelect";
import useReadChat from "@/composables/v2.1/useReadChat";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";

// Composables V2.1
import useTags from "@/composables/v2.1/useTags";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
import axios from "@/libs/axios";
import axiosV2 from "@/libs/axiosV2";
import i18n from "@/libs/i18n";
import luxon from "@/libs/luxon";
import { useWebsockets } from "@/libs/websockets";
import router from "@/router";
import { StatusFilterNameType } from "@/types/filter.types";
import { InboxModuleType } from "@/types/inboxModule.type";
import { shouldRenewSignedCookies } from "@/utils/assets";
import { supportsMessageStatusUpdates } from "@/utils/channels";

// Utils
import {
    isChatArchived,
    isChatAssignedToMeV21,
    resolveChatStatusFilter,
    resolveEntryContent,
    resolveEntryType,
    resolveRemainingCustomerCareWindow,
} from "@/utils/chats";
import chatTransformer from "@/utils/chatTransformer";
import { decrypt } from "@/utils/crypto";
import activeChatSidebars from "@/views/components/inbox/active-chat-sidebars/activeChatSidebars";
import activeChatSidebarApps from "@/views/components/inbox/active-chat-sidebars/apps";
import activeChatSidebarContact from "@/views/components/inbox/active-chat-sidebars/contact";
import activeChatSidebarInternalComments from "@/views/components/inbox/active-chat-sidebars/internalComments";
import activeChatSidebarTags from "@/views/components/inbox/active-chat-sidebars/tags";
import activeChatAlerts from "@/views/components/inbox/active-chat/activeChatAlerts";
import activeChatAssetPreview from "@/views/components/inbox/active-chat/activeChatAssetPreview";
import activeChatCompose from "@/views/components/inbox/active-chat/activeChatCompose";
import activeChatNavbar from "@/views/components/inbox/active-chat/activeChatNavbar";
import chatLeftSidebar from "@/views/components/inbox/chat-left-sidebar/chatLeftSidebar";
import useChatsListSorting from "@/views/components/inbox/chat-left-sidebar/useChatsListSorting";
import startChatPlaceholder from "@/views/components/inbox/startChatPlaceholder";

// Components
import activeChatLog from "@/views/modules/email-inbox/activeChatLog";
import { useResponsiveAppLeftSidebarVisibility } from "@core/comp-functions/ui/app";
import { useQuery } from "@tanstack/vue-query";
// @ts-ignore
import { $themeBreakpoints } from "@themeConfig";
import { computed, nextTick, onMounted, ref } from "@vue/composition-api";
import { useBreakpoints } from "@vueuse/core";
import { BAlert, BAvatar, BButton, BOverlay } from "bootstrap-vue";
import { findLast, isArray } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BButton,
        BAvatar,
        BAlert,
        BOverlay,

        activeChatLog,
        activeChatCompose,
        activeChatAssetPreview,
        chatLeftSidebar,
        activeChatAlerts,
        startChatPlaceholder,
        activeChatNavbar,
        activeChatSidebars,
        activeChatSidebarContact,
        activeChatSidebarTags,
        activeChatSidebarInternalComments,
        activeChatSidebarApps,
    },
    setup(props, { root }) {
        const { setInboxModuleType, inboxModulePath, publicApiEndpointPath } = useInboxModuleType();
        setInboxModuleType(InboxModuleType.EMAIL);

        const toast = useToast();
        const { websockets } = useWebsockets();

        const { filters, setActiveFilter, getActiveFilter, smartInboxAvailable } = useEmailFilters();
        const {
            loadActiveChat,
            getActiveChat,
            updateActiveChat,
            findActiveChatLastConversation,
            closeActiveChat,
            addActiveChatEntry,
            isLoading: isLoadingActiveChat,
            intersectElementVisible,
            page: activeChatPage,
        } = useActiveChat();
        const { contacts } = useContacts();

        const { loadChats, getChats, updateChatInChatsList, setChatsListLastEntry, clearChats } = useChats();
        clearChats();

        const { uploadFiles, removeFile, getFiles, clearFiles, isLoading: isLoadingFiles } = useFileUploads();
        const { setRead } = useReadChat(toast);
        const { emailStoredMessages, removeStoredMessage } = useStoredMessages();
        const {
            loadInternalComments,
            getInternalComments,
            createInternalComment,
            updateInternalComment,
            deleteInternalComment,
        } = useActiveChatInternalComments();
        const { teams, users, teamsOfUser, getRandomUserTeamForChannel, isUserPartOfTeam } = useUsers();
        const { channels, fetchChannels } = useChannels();
        const { tags } = useTags();
        const { getUserSetting } = useUserSettings();
        const { isTemplate, isQuickReply, selectedTemplate, hasMediaTemplateFilesUploaded } =
            useActiveChatTemplates(getActiveChat);

        const chatEntryInput = ref({
            message: "",
            metadata: {
                to: [],
                cc: [],
                bcc: [],
                subject: "",
                externalReference: "",
            },
        });
        const isAssignChatPending = ref(false);

        const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility();
        const shallShowActiveChatSidebarContact = ref(false);
        const shallShowActiveChatSidebarTags = ref(false);
        const shallShowActiveChatSidebarInternalComments = ref(false);
        const shallShowActiveChatSidebarApps = ref(false);
        const mobilePluginBarActive = ref(false);

        onMounted(() => {
            // On small screens, if no chat id is set, we want to show the left sidebar instead of the zero state
            if (!router.currentRoute.params.chatId && useBreakpoints($themeBreakpoints).isSmaller("lg")) {
                mqShallShowLeftSidebar.value = true;
            }

            fetchChannels({
                params: {
                    join: ["userChannels", "teamChannels"],
                },
            });
        });

        const scrollContainer = ref(null);
        const scrollToBottomInActiveChatLog = () => {
            scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
        };

        // It's possible to have an active chat without having an ID in the query string, for example when switching from settings back to inbox.
        if (getActiveChat.value) {
            closeActiveChat();
        }

        const { isInitialLoading } = useQuery({
            queryKey: ["emailChats"],
            queryFn: () => {
                // Only show loader when it's the initial loading, when refetching (refocus window), do not show loader.
                // @notice undefined check, because switching between modules won't reset the initial value of isInitialLoading (will stay false because of undefined)
                const initLoad = typeof isInitialLoading === "undefined";
                return loadChats(getActiveFilter, { isInitialLoading: typeof isInitialLoading === "undefined" }).then(
                    (result) => {
                        if (router.currentRoute.params.chatId) {
                            openChat(
                                { chatId: Number(router.currentRoute.params.chatId) },
                                { isInitialLoading: initLoad }
                            );
                        }

                        return result;
                    }
                );
            },
            refetchOnWindowFocus: false,
            // staleTime: typeof isInitialLoading !== "undefined" ? 5000 : 0,
        });

        const applyFilter = ({ filter }, options) => {
            setActiveFilter(filter);
            return loadChats(getActiveFilter, options);
        };

        const openChat = ({ chatId, channelId, identityId, read }, options) => {
            if (!chatId) {
                throw new Error("No chatId passed.");
            }

            // Check if there's a message stored in the localStorage so we can load that.
            const storedMessage = emailStoredMessages.value?.find((item) => item.chatId === chatId);
            if (storedMessage) {
                chatEntryInput.value.message = storedMessage.message;
            } else {
                chatEntryInput.value.message = "";
            }

            loadActiveChat(chatId, channelId, identityId, options)
                .then(async (activeChat) => {
                    // Check if the signed cookies going to be expire, if so, fetch new signed cookies.
                    if (shouldRenewSignedCookies()) {
                        useAws.getSignedCookies(getUserData().storageName).then((signedCookies) => {
                            const { setSignedCookies } = useCookies();
                            setSignedCookies(signedCookies);
                        });
                    }

                    loadInternalComments(activeChat.identity.id, {
                        fields: "id,userId,comment,createdAt,editedAt",
                    });

                    if (options?.isInitialLoading) {
                        const lastChatLogEntry = activeChat.chatLog[activeChat.chatLog.length - 1];
                        const chatStatusFilter = resolveChatStatusFilter({
                            assignedUserId: lastChatLogEntry.assignedUserId,
                            assignedTeamId: lastChatLogEntry.assignedTeamId,
                            lastConversation: {
                                status: lastChatLogEntry.status,
                            },
                        });

                        // Await because we can only setRead below when the chats list is (re)fetched what is done by the applyFilter.
                        const { statusFilters } = useEmailStatusFilters();
                        await applyFilter(
                            {
                                filter: {
                                    status: statusFilters[chatStatusFilter[0]],
                                },
                                page: 1,
                            },
                            options
                        );
                    }

                    if (!read) {
                        setRead(
                            {
                                id: chatId,
                                channel: {
                                    platform: formattedActiveChat.value.channel.platform,
                                },
                            },
                            { showToast: false }
                        );
                    }
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.status === 404
                            ? "entities.conversation.could-not-find"
                            : "entities.conversation.could-not-open";
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.conversation.Conversation"),
                            text: i18n.t(errorMessage),
                            icon: "times",
                            variant: "danger",
                        },
                    });

                    if (router.currentRoute.params.chatId) {
                        router.replace({
                            name: inboxModulePath?.value,
                        });
                    }
                });

            if (chatId !== Number(router.currentRoute.params.chatId)) {
                router.replace({
                    name: inboxModulePath?.value,
                    params: {
                        chatId,
                    },
                });
            }
        };

        const isSendPending = ref(false);
        const sendMessage = () => {
            if (getFiles.value?.length === 0 && !chatEntryInput.value.message) {
                return;
            }

            isSendPending.value = true;

            // Get out of archive when it's archived.
            if (isChatArchived(formattedActiveChat.value)) {
                unarchiveChat({
                    id: formattedActiveChat.value.lastConversation.id,
                    channel: {
                        id: formattedActiveChat.value.channel.id,
                        platform: formattedActiveChat.value.channel.platform,
                    },
                    identityId: formattedActiveChat.value.identity.id,
                    status: formattedActiveChat.value.lastConversation.status,
                });
            }

            let assigning;
            if (!isChatAssignedToMeV21(formattedActiveChat.value.lastConversation.assignedUserId)) {
                assigning = assignChat({
                    id: formattedActiveChat.value.lastConversation.id,
                    channel: {
                        id: formattedActiveChat.value.channel.id,
                        platform: formattedActiveChat.value.channel.platform,
                    },
                    identityId: formattedActiveChat.value.identity.id,
                    userId: getUserData().id,
                    teamId:
                        formattedActiveChat.value.lastConversation.assignedTeamId &&
                        isUserPartOfTeam(formattedActiveChat.value.lastConversation.assignedTeamId)
                            ? formattedActiveChat.value.lastConversation.assignedTeamId
                            : getRandomUserTeamForChannel(formattedActiveChat.value.channel.id),
                    status: formattedActiveChat.value.lastConversation.status,
                });
            } else {
                assigning = new Promise((resolve) => resolve(true));
            }

            /*
PREPARE MESSAGE
*/
            const { transformToOutboundEntry } = chatTransformer(formattedActiveChat.value);
            const outboundChatEntry = transformToOutboundEntry(chatEntryInput.value, getFiles.value, {
                isTemplate: isTemplate.value,
                isQuickReply: isQuickReply.value,
                selectedTemplate: selectedTemplate.value,
            });

            const tmpId = uuidv4(); // Because we add entry to chat log BEFORE we got backend response, we need a temporary id, so we can identify the newly created entry to update with a new status after the backend request finished.

            let payload;
            if (
                resolveEntryType(outboundChatEntry.payload.content) === "asset" &&
                outboundChatEntry.payload.content.attachments.length > 0
            ) {
                payload = {
                    type: resolveEntryType(outboundChatEntry.payload.content),
                    id: tmpId,
                    identityId: formattedActiveChat.value.identity.id,
                    time: luxon.DateTime.now().toUTC().toString(),
                    direction: "out",
                    status: supportsMessageStatusUpdates(formattedActiveChat.value.channel) ? "pending" : "none",
                    userId: formattedActiveChat.value.assignee?.id ?? getUserData().id,
                    components: selectedTemplate.value?.components,
                    caption: outboundChatEntry.payload.content.text,
                    assets: outboundChatEntry.payload.content.attachments?.map((attachment) => ({
                        contentType: attachment.contentType,
                        filePath: attachment.filePath,
                        fileName: attachment.fileName,
                        originalName: attachment.originalName,
                        category: attachment.category,
                    })),
                };
            } else {
                payload = {
                    direction: "out",
                    id: tmpId,
                    identityId: formattedActiveChat.value.identity.id,
                    message: outboundChatEntry.payload.content.text,
                    status: supportsMessageStatusUpdates(formattedActiveChat.value.channel) ? "pending" : "none",
                    time: luxon.DateTime.now().toUTC().toString(),
                    type: resolveEntryType(outboundChatEntry.payload.content),
                    userId: formattedActiveChat.value.assignee?.id ?? getUserData().id,
                    components: selectedTemplate.value?.components,
                };
            }

            addActiveChatEntry(payload);

            /*
RESET STUFF
*/
            chatEntryInput.value = {
                message: "",
                metadata: {
                    to: [],
                    cc: [],
                    bcc: [],
                    subject: "",
                    externalReference: "",
                },
            };
            clearFiles();
            useActiveChatTemplates(getActiveChat.value).resetTemplate(); // @NOTE old -- but left it for now.

            nextTick(scrollToBottomInActiveChatLog);

            // Retroactively update the "pre-sent" (aka temporary entry) entry
            const tmpEntry = getActiveChat.value.chatLog
                .find((conversation) => conversation.id === formattedActiveChat.value.lastConversation.id)
                .entries.find((entry) => entry.id === tmpId);

            axiosV2
                .post(`/messages/send`, outboundChatEntry.payload)
                .then((response) => {
                    assigning.then(() => {
                        setRead(
                            {
                                id: formattedActiveChat.value.lastConversation.id,
                                channel: {
                                    platform: formattedActiveChat.value.channel.platform,
                                },
                            },
                            {
                                showToast: false,
                                isNewActiveChatEntry: true,
                            }
                        );
                    });

                    // Response: externalReference, id, status, time
                    tmpEntry.id = response.data.id; // Update with definitive ID.
                    tmpEntry.metadata = response.data.metadata;
                    // Update status here, if we got a status back, to deal with immediate rejections.
                    tmpEntry.status = response.data.status ?? tmpEntry.status;
                    tmpEntry.statusReason = response.data.statusReason ?? tmpEntry.statusReason;

                    removeStoredMessage(formattedActiveChat.value.lastConversation.id);

                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.email.email-message"),
                            text: i18n.t("entities.email.sent-successfully"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch(() => {
                    tmpEntry.status = "declined";

                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.email.email-message"),
                            text: i18n.t("entities.email.sent-error"),
                            icon: "times",
                            variant: "error",
                        },
                    });
                })
                .finally(() => (isSendPending.value = false));

            // --- HANDLE CHATS LIST TO ADD LAST ENTRY ---
            const newEntryType = resolveEntryType(outboundChatEntry.payload.content);
            setChatsListLastEntry(
                formattedActiveChat.value?.lastConversation.id,
                {
                    id: tmpId,
                    direction: "out",
                    message: outboundChatEntry.payload.content.text, // @todo caption of asset.
                    time: luxon.DateTime.now().toUTC().toString(), // ISO datetime
                    type: newEntryType,
                    isAsset: newEntryType === "asset" ? 1 : 0,
                },
                true
            );

            isSendPending.value = false;
        };

        const formattedActiveChat = computed(() => {
            // @NOTE this computed can be run earlier than the activeChat is loaded.
            if (!getActiveChat.value) {
                return;
            }

            // Can be done via backend, but decided to do this in the frontend because of time.
            const lastConversation = getActiveChat.value.chatLog[getActiveChat.value.chatLog.length - 1];
            const lastInboundEntry = findLast(lastConversation.entries, (entry) => entry.direction === "in");

            const formattedActiveChat = {
                ...getActiveChat.value,
                channel: channels.value.find((channel) => channel.id === getActiveChat.value.channelId),
                lastConversation,
                lastInboundMessageTime: lastInboundEntry?.time, // if we "start new chat" from contacts, we may not have lastInboundEntry and we act like CCW is closed.
                channelId: undefined,
                contactId: undefined,
            };

            delete formattedActiveChat.channelId;
            delete formattedActiveChat.contactId;
            return formattedActiveChat;
        });

        const teamsFilterFunction = (chat, teamIds) => {
            if (chat.teams.length > 0) {
                const chatTeamIds = chat.teams?.map((chatTeam) => chatTeam?.id);
                if (teamIds.find((teamId) => chatTeamIds.includes(teamId))) {
                    return chat;
                }
            }
        };

        const tagsFilterFunction = (chat) => {
            if (chat.tags.length > 0) {
                const chatTagIds = chat.tags?.map((chatTag) => chatTag?.id);
                if (filters.tags.find((tagId) => chatTagIds.includes(tagId))) {
                    return chat;
                }
            }
        };

        const channelsFilterFunction = (chat) => filters.channels?.find((channelId) => channelId === chat.channel?.id);

        const formattedChatsList = computed(() => {
            const { multiSelectEnabled, filterMultiSelectChats } = useMultiSelect();
            if (multiSelectEnabled.value) {
                // This makes sure that no multi select chat is still selected when it's changed already (by another agent), for example when I was
                // at the settings module and went back or when changing channel settings (enable/disable deletion of chats).
                filterMultiSelectChats(getChats.value);
            }

            let formattedChatsList = getChats.value.map((chat) => {
                const channel = channels.value?.find((item) => item.id === chat.channelId);
                const team = teams.value?.find((item) => item.id === chat.assignedTeamId);

                let formatted = {
                    ...chat,
                    channel,
                    contact: contacts.value.find((contact) => contact.id === chat.contactId),
                    assignee: users.value.find((agent) => agent.id === chat.assignedUserId),
                    assignedTeam: team,
                    teams: [team],
                    tags: tags.value
                        .map((tag) => (chat.tagIds.includes(tag.id) ? tag : undefined))
                        .filter((tag) => tag),
                    resolveRemainingCustomerCareWindow: resolveRemainingCustomerCareWindow(
                        chat.lastInboundMessageTime,
                        channel?.customerCareWindowLength
                    ),
                    storedMessage: emailStoredMessages.value?.find((item) => item.chatId === chat.id)?.message ?? "",
                };

                // Delete properties if we don't need them anymore. Chats will only have full objects what we can use.
                delete formatted.channelId;
                delete formatted.contactId;
                delete formatted.assignedTeamId;
                delete formatted.tagIds;

                return formatted;
            });

            const enforceTeamFilter = getUserSetting("enforceTeamFilter", "0") === "1";
            if (filters.teams?.length > 0 || enforceTeamFilter) {
                const teamIds =
                    enforceTeamFilter && filters.teams.length === 0
                        ? teams.value.reduce((list, team) => {
                              if (team.teamUsers.some((teamUser) => teamUser.userId === getUserData().id)) {
                                  list.push(team.id);
                              }
                              return list;
                          }, [])
                        : filters.teams;
                formattedChatsList = formattedChatsList.filter((chat) => teamsFilterFunction(chat, teamIds));
            }

            if (filters.channels?.length > 0) {
                formattedChatsList = formattedChatsList.filter(channelsFilterFunction);
            }

            if (filters.tags?.length > 0) {
                formattedChatsList = formattedChatsList.filter(tagsFilterFunction);
            }

            if (getActiveFilter.value.status) {
                formattedChatsList = formattedChatsList.filter((chat) =>
                    chat.statusFilter.includes(getActiveFilter.value.status.name)
                );
            }

            return useChatsListSorting(formattedChatsList, smartInboxAvailable.value, getActiveFilter);
        });

        const assignChat = async (chat, options = { showToast: true }) => {
            const assignResults = { ok: [], error: [] };
            const chats = isArray(chat) ? chat : [chat];
            const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();

            if (!chats[0]) {
                return;
            }

            isAssignChatPending.value = true;

            // @NOTICE just chats key 0, because all chats are assigned to the same user/team.
            const user = chats[0].userId && users.value.find((agent) => agent.id === chats[0].userId);
            const team = chats[0].teamId && teams.value.find((team) => team.id === chats[0].teamId);

            const { updateUnreadChat } = useUnreadChats();
            await Promise.all(
                chats.map(async (chat) => {
                    if (!chat.id) {
                        throw new Error("No id passed.");
                    }

                    updateUnreadChat({
                        chatId: chat.id,
                        channelId: chat.channelId,
                        tagIds: chat.tags?.map((tag) => tag.id),
                        assignedUserId: chat.userId,
                        assignedTeamId: chat.teamId,
                        status: chat.status,
                    });

                    // Can may be done better, but left it for now because of time.
                    let chatLog;
                    if (getActiveChat.value) {
                        const activeChatConversation = findActiveChatLastConversation();
                        chatLog = [...getActiveChat.value.chatLog];
                        chatLog[activeChatConversation.index] = {
                            ...chatLog[activeChatConversation.index],
                            assignedUserId: chat.userId,
                            assignedTeamId: chat.teamId,
                        };
                    }

                    // We have to await here, otherwise the assignResults counters are not set correctly.
                    await Promise.all([
                        // @NOTE this will be done via the incoming websocket event, so commented for now.
                        updateChatInChatsList(chat.id, {
                            assignedUserId: user?.id,
                            assignedTeamId: team?.id,
                            statusFilter: resolveChatStatusFilter(
                                {
                                    assignedUserId: chat.userId,
                                    assignedTeamId: chat.teamId,
                                    lastConversation: {
                                        status: "open",
                                    },
                                },
                                teamsOfUser.value
                            ),
                        }),
                        updateActiveChat({
                            ...getActiveChat.value,
                            chatLog,
                        }),
                        axios.post(
                            `/${publicApiEndpointPath.value}/${chat.userId || chat.teamId ? "assign" : "unassign"}`,
                            {
                                chatId: chat.id,
                                userId: chat.userId ?? null,
                                teamId: chat.teamId ?? null,
                            }
                        ),
                    ])
                        .then(() => assignResults.ok.push(chat))
                        .catch(() => assignResults.error.push(chat))
                        .finally(() => (isAssignChatPending.value = false));
                })
            );

            if (multiSelectEnabled.value) {
                clearMultiSelect(assignResults.ok);
            }

            // Every action has it's own specific notification, looks a spaghetti you know.
            if (options.showToast) {
                let status = "success";

                let notificationText;
                if (user?.name || team?.name) {
                    notificationText = i18n.t("entities.conversation.assigned-to-amount", {
                        userName: user?.name ?? team?.name ?? "unknown",
                        amount: assignResults.ok.length,
                        entity: i18n.tc("entities.conversation.conversations", assignResults.ok.length),
                    });
                } else {
                    notificationText = i18n.t("entities.conversation.unassigned-to-amount", {
                        amount: assignResults.ok.length,
                        entity: i18n.tc("entities.conversation.conversations", assignResults.ok.length),
                    });
                }

                if (assignResults.ok.length > 0 && assignResults.error.length > 0) {
                    status = "warning";

                    if (user?.name || team?.name) {
                        notificationText = i18n.t("assigned-to-multiple-amount", {
                            userName: user?.name ?? team?.name ?? "unknown",
                            amountOk: assignResults.ok.length,
                            entityOk: i18n.tc("entities.conversation.conversations", assignResults.ok.length),
                            amountError: assignResults.error.length,
                            entityError: i18n.tc("entities.conversation.conversations", assignResults.error.length),
                        });
                    } else {
                        notificationText = i18n.t("unarchived-multiple-amount", {
                            amountOk: assignResults.ok.length,
                            entityOk: i18n.tc("entities.conversation.conversations", assignResults.ok.length),
                            amountError: assignResults.error.length,
                            entityError: i18n.tc("entities.conversation.conversations", assignResults.error.length),
                        });
                    }
                } else if (assignResults.ok.length === 0 && assignResults.error.length > 0) {
                    status = "error";

                    if (user?.name || team?.name) {
                        notificationText = i18n.t("entities.conversation.could-not-assign-amount", {
                            amount: assignResults.error.length,
                            entity: i18n.tc("entities.conversation.conversations", assignResults.error.length),
                        });
                    } else {
                        notificationText = i18n.t("entities.conversation.could-not-unassign-amount", {
                            amount: assignResults.error.length,
                            entity: i18n.tc("entities.conversation.conversations", assignResults.error.length),
                        });
                    }
                }

                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Assign"),
                        text: notificationText,
                        icon: status === "error" ? "times" : "check",
                        variant: status,
                    },
                });

                if (assignResults.error.length !== 0) {
                    return;
                }

                const { statusFilters } = useEmailStatusFilters();
                if (chats[0].userId === getUserData().id) {
                    applyFilter({
                        filter: {
                            status: statusFilters[StatusFilterNameType.PERSONAL],
                        },
                        page: 1,
                    });
                } else {
                    closeActiveChat();
                }

                if (chats[0].userId === null && chats[0].teamId === null) {
                    applyFilter({
                        filter: {
                            status: statusFilters[StatusFilterNameType.NEW],
                        },
                        page: 1,
                    });
                }
            }
        };

        /**
         * UNARCHIVE CHAT
         */
        const { isArchiveChatPending } = useArchiveChat();
        const unarchiveChat = async (chat, options = { showToast: true }) => {
            const unarchiveResults = { ok: [], error: [] };
            const chats = isArray(chat) ? chat : [chat];
            const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();

            if (!chats[0]) {
                return;
            }

            isArchiveChatPending.value = true;

            const { statusFilters, resolveUnreadChatStatusFilter } = useEmailStatusFilters();
            const { addUnreadChat } = useUnreadChats();
            await Promise.all(
                chats.map(async (chat) => {
                    if (!chat.id) {
                        throw new Error("No id passed.");
                    }

                    if (!chat.read) {
                        resolveUnreadChatStatusFilter({
                            chatId: chat.id,
                            channelId: chat.channelId,
                            tagIds: chat.tags?.map((tag) => tag.id),
                            assignedUserId: chat.assignedUserId,
                            assignedTeamId: chat.assignedTeamId,
                            status: chat.status,
                        }).forEach(() => {
                            addUnreadChat({
                                chatId: chat.id,
                                channelId: chat.channelId,
                                tagIds: chat.tags?.map((tag) => tag.id),
                                assignedUserId: chat.assignedUserId,
                                assignedTeamId: chat.assignedTeamId,
                                status: chat.status,
                            });
                        });
                    }

                    let chatLog;
                    if (getActiveChat.value && findActiveChatLastConversation()?.data.id === chat.id) {
                        const activeChatConversation = findActiveChatLastConversation();
                        chatLog = [...getActiveChat.value.chatLog];
                        chatLog[activeChatConversation.index] = {
                            ...chatLog[activeChatConversation.index],
                            status: "open",
                        };
                    }

                    await Promise.all([
                        axios.post(`/${publicApiEndpointPath.value}/unarchive`, { chatId: chat.id }),
                        chatLog
                            ? updateActiveChat({
                                  ...getActiveChat.value,
                                  chatLog,
                              })
                            : false,
                        updateChatInChatsList(chat.id, {
                            statusFilter: resolveChatStatusFilter(
                                {
                                    lastConversation: {
                                        status: "open",
                                    },
                                },
                                teamsOfUser.value
                            ),
                        }),
                    ])
                        .then(([updatedConversation]) => {
                            if (getActiveChat.value && findActiveChatLastConversation()?.data.id === chat.id) {
                                const chatStatusFilter = resolveChatStatusFilter(
                                    {
                                        assignedUserId: formattedActiveChat.value.lastConversation.assignedUserId,
                                        assignedTeamId: formattedActiveChat.value.lastConversation.assignedTeamId,
                                        lastConversation: {
                                            status: formattedActiveChat.value.lastConversation.status,
                                        },
                                    },
                                    teamsOfUser.value
                                );

                                applyFilter({
                                    filter: {
                                        status: statusFilters[chatStatusFilter[0]],
                                    },
                                    page: 1,
                                });
                            }

                            unarchiveResults.ok.push(chat);

                            if (!multiSelectEnabled && !router.currentRoute.params.chatId) {
                                router.replace({
                                    name: inboxModulePath?.value,
                                    params: {
                                        chatId: chat.conversationId,
                                    },
                                });
                            }

                            // If we are, maybe per accident, on an old archived chat, update the URL with the newest open conversation.
                            if (
                                !multiSelectEnabled &&
                                updatedConversation.data.id !== parseInt(router.currentRoute.params.chatId)
                            ) {
                                router.replace({
                                    name: inboxModulePath?.value,
                                    params: {
                                        chatId: updatedConversation.data.id,
                                    },
                                });
                            }
                        })
                        .catch(() => unarchiveResults.error.push(chat))
                        .finally(() => (isArchiveChatPending.value = false));
                })
            );

            if (multiSelectEnabled.value) {
                clearMultiSelect(unarchiveResults.ok);
            }

            if (options.showToast) {
                let status = "success";
                let notificationText = i18n.t("entities.conversation.is-unarchived-amount", {
                    amount: unarchiveResults.ok.length,
                    entity: i18n.tc("entities.conversation.conversations", unarchiveResults.ok.length),
                });

                if (unarchiveResults.ok.length > 0 && unarchiveResults.error.length > 0) {
                    status = "warning";
                    notificationText = i18n.t("unarchived-multiple-amount", {
                        amountOk: unarchiveResults.ok.length,
                        entityOk: i18n.tc("entities.conversation.conversations", unarchiveResults.ok.length),
                        amountError: unarchiveResults.error.length,
                        entityError: i18n.tc("entities.conversation.conversations", unarchiveResults.error.length),
                    });
                } else if (unarchiveResults.ok.length === 0 && unarchiveResults.error.length > 0) {
                    status = "error";
                    notificationText = i18n.t("entities.conversation.could-not-unarchive-amount", {
                        amount: unarchiveResults.error.length,
                        entity: i18n.tc("entities.conversation.conversations", unarchiveResults.error.length),
                    });
                }

                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Unarchive"),
                        text: notificationText,
                        icon: status === "error" ? "times" : "check",
                        variant: status,
                    },
                });
            }
        };

        const localUploadFiles = (assets) => {
            uploadFiles(assets, formattedActiveChat.value.channel).then(() => {
                if (selectedTemplate.value.mediaType) {
                    hasMediaTemplateFilesUploaded.value = true;
                }
            });
        };

        const updateLinkedTags = (linkedTags) => {
            Promise.all([
                axios.post(`/email-chats/${formattedActiveChat.value.lastConversation.id}/tags`, linkedTags),
                updateActiveChat({
                    ...getActiveChat.value,
                    tags: linkedTags,
                }),
                updateChatInChatsList(formattedActiveChat.value.lastConversation.id, {
                    tags: linkedTags,
                }),
            ])
                .then(() =>
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.tags.Tags"),
                            text: i18n.t("entities.tags.tag-linked-success"),
                            icon: "check",
                            variant: "success",
                        },
                    })
                )
                .catch(() =>
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.tags.Tags"),
                            text: i18n.t("entities.tags.tag-linked-error"),
                            icon: "times",
                            variant: "danger",
                        },
                    })
                );
        };

        const { deleteChat } = useDeleteChat(root.$bvModal, toast);
        return {
            formattedChatsList,
            formattedActiveChat,

            internalComments: getInternalComments,

            // Loading states from composables
            isLoadingActiveChat,
            isLoadingFiles,
            isSendPending,
            isAssignChatPending,
            isArchiveChatPending,

            // Filters
            filters,
            getActiveFilter,
            smartInboxAvailable,

            // UI
            mqShallShowLeftSidebar,
            scrollToBottomInActiveChatLog,

            // Methods
            openChat,
            applyFilter,
            assignChat,
            unarchiveChat,
            deleteChat,
            setRead,

            // Sidebar states
            shallShowActiveChatSidebarApps,
            shallShowActiveChatSidebarContact,
            shallShowActiveChatSidebarInternalComments,
            shallShowActiveChatSidebarTags,
            mobilePluginBarActive,

            // Message compose
            chatEntryInput,
            sendMessage,

            getFiles,
            updateLinkedTags,
            removeFile,

            createInternalComment,
            deleteInternalComment,
            updateInternalComment,

            websockets,
            intersectElementVisible,
            activeChatPage,
            localUploadFiles,

            toast,
            scrollContainer,

            InboxModuleType,
        };
    },
    sockets: {
        socketEvent: async function (socketMessage) {
            const wsMessage = decrypt(socketMessage.event, socketMessage.iv);

            // Do not process events that is coming from the origin sender or is not from messaging inbox.
            if (
                wsMessage.senderId === this.websockets.id ||
                (wsMessage.payload.inboxModuleType && wsMessage.payload.inboxModuleType !== InboxModuleType.EMAIL)
            ) {
                return;
            }

            let assignee, contact;
            const { updateUserStatus, users, teamsOfUser } = useUsers();
            const { contacts, addContact, updateContact } = useContacts();
            const { addActiveChatEntry, closeActiveChat } = useActiveChat();
            const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();
            const { getActiveFilter } = useEmailFilters();
            const { setChatsListLastEntry, deleteChatFromChatsList, addChatToChatsList, updateChatInChatsList } =
                useChats();

            switch (wsMessage.type) {
                case "receivedMessage":
                    if (wsMessage.payload.user?.id === getUserData().id) {
                        break;
                    }

                    if (wsMessage.payload.conversation.id !== this.formattedActiveChat?.lastConversation.id) {
                        updateChatInChatsList(wsMessage.payload.conversation.id, {
                            read: false,
                        }).then(() => {
                            const statusFilter = resolveChatStatusFilter(
                                {
                                    assignedUserId: wsMessage.payload.conversation.assignedUser,
                                    assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                                    lastConversation: {
                                        status:
                                            wsMessage.payload.conversation.conversationStatus ??
                                            wsMessage.payload.conversation.status,
                                    },
                                },
                                teamsOfUser
                            );

                            // Are we currently on the same status as the to-update chat AND is the chat already in this filter?
                            // Do nothing. Above is the chat updated, so that's enough.
                            // If not, add it the the chats list, so reactivity will be activated and the counters updated.
                            if (
                                statusFilter.includes(getActiveFilter.value.status.name) &&
                                this.formattedChatsList.find(
                                    (formattedChat) => formattedChat.id === wsMessage.payload.conversation.id
                                )
                            ) {
                                return;
                            }

                            addChatToChatsList({
                                assignedUserId: wsMessage.payload.conversation.assignedUser,
                                assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                                channelId: wsMessage.payload.conversation.channelId,
                                contactId: wsMessage.payload.conversation.contactId,
                                id: wsMessage.payload.conversation.id,
                                identity: {
                                    id: wsMessage.payload.conversation.identityId,
                                    identity: wsMessage.payload.conversation.identity,
                                },
                                lastConversation: {
                                    status:
                                        wsMessage.payload.conversation.conversationStatus ??
                                        wsMessage.payload.conversation.status,
                                    id: wsMessage.payload.conversation.id,
                                    lastEntry: {
                                        direction: wsMessage.payload.conversation.direction,
                                        id: wsMessage.payload.conversation.messageId,
                                        isAsset: wsMessage.payload.message.isAsset,
                                        // locationId: // @todo
                                        message: wsMessage.payload.conversation.message,
                                        time: wsMessage.payload.message.time,
                                        type: resolveEntryType(wsMessage.payload.message),
                                    },
                                },
                                lastInboundMessageTime: wsMessage.payload.message.time,
                                platform: wsMessage.payload.conversation.platform,
                                read: false,
                                tagIds: [],
                                unreadMessages: 1,
                                statusFilter,
                            });
                        });
                    }

                    // @TEMP, because this should be combined with the setChatsListLastEntry below.
                    updateChatInChatsList(wsMessage.payload.conversation.id, {
                        lastInboundMessageTime: wsMessage.payload.message.time,
                        assignedUserId: wsMessage.payload.conversation.assignedUser,
                        assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                    });

                    if (wsMessage.payload.conversation.contactId && wsMessage.payload.conversation.contactName) {
                        updateContact({
                            id: wsMessage.payload.conversation.contactId,
                            name: wsMessage.payload.conversation.contactName,
                        });
                    }

                    // If a chat has moved from our current filter to another filter we need to remove it from the current filter
                    if (
                        !resolveChatStatusFilter(
                            {
                                assignedUserId: wsMessage.payload.conversation.assignedUser,
                                assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                                lastConversation: {
                                    status:
                                        wsMessage.payload.conversation.conversationStatus ??
                                        wsMessage.payload.conversation.status,
                                },
                            },
                            teamsOfUser
                        ).includes(getActiveFilter.value.status.name)
                    ) {
                        deleteChatFromChatsList(wsMessage.payload.conversation.id);
                    }

                    // Chats list last entry
                    setChatsListLastEntry(
                        wsMessage.payload.conversation.id,
                        {
                            id: wsMessage.payload.message.id,
                            direction: wsMessage.payload.message.direction,
                            message: wsMessage.payload.message.message, // @todo caption of asset.
                            time: wsMessage.payload.message.time, // ISO datetime
                            type: resolveEntryType(wsMessage.payload.message),
                            isAsset: wsMessage.payload.message.isAsset,
                        },
                        wsMessage.payload.conversation.id === this.formattedActiveChat?.lastConversation.id
                    );

                    /*
ACTIVE CHAT
*/
                    if (this.formattedActiveChat?.lastConversation.id === wsMessage.payload.conversation.id) {
                        addActiveChatEntry(resolveEntryContent(wsMessage.payload.message));

                        this.setRead(
                            {
                                id: wsMessage.payload.conversation.id,
                                channel: {
                                    platform: wsMessage.payload.conversation.platform,
                                },
                            },
                            {
                                showToast: false,
                                isNewActiveChatEntry: true,
                            }
                        );

                        nextTick(this.scrollToBottomInActiveChatLog);
                    }

                    break;
                case "newConversation":
                    // Do we have an active chat which is archived and a new conversation is coming in from the same identity id,
                    // then close the active chat, so we need to continue with the newly created chat.
                    if (this.formattedActiveChat?.lastConversation.id === wsMessage.payload.conversation.id) {
                        closeActiveChat(wsMessage.payload.conversation.id);

                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("entities.conversation.Assign"),
                                text: i18n.t("entities.conversation.changed"),
                                icon: "exclamation-triangle",
                                variant: "warning",
                            },
                        });
                    }

                    if (wsMessage.payload.conversation.assignedUser) {
                        assignee = users.value.find((user) => user.id === wsMessage.payload.conversation.assignedUser);
                    }

                    contact = contacts.value.find((contact) => contact.id === wsMessage.payload.conversation.contactId);
                    if (!contact) {
                        // Fetch from backend and add it to the contacts state.
                        contact = await addContact(wsMessage.payload.conversation.contactId);
                    }

                    // Are we on "archive" AND is there already a previous conversation of the same identity,
                    // Remove the chat from the archived chats list.
                    if (getActiveFilter.value.status.name === "archive") {
                        const previousArchivedChat = this.formattedChatsList.find(
                            (chat) => chat.identity.id === wsMessage.payload.conversation.identityId
                        );

                        deleteChatFromChatsList(previousArchivedChat.id);

                        router.replace({ name: inboxModulePath?.value });
                    }

                    // @todo @v21 only when active status filter equals to ws payload conversation status
                    addChatToChatsList({
                        assignedUserId: wsMessage.payload.conversation.assignedUser,
                        assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                        channelId: wsMessage.payload.conversation.channelId,
                        contactId: contact.id,
                        id: wsMessage.payload.conversation.id,
                        identity: {
                            id: wsMessage.payload.conversation.identityId,
                            identity: wsMessage.payload.conversation.identity,
                        },
                        lastConversation: {
                            status:
                                wsMessage.payload.conversation.conversationStatus ??
                                wsMessage.payload.conversation.status,
                            id: wsMessage.payload.conversation.id,
                            lastEntry: {
                                direction: wsMessage.payload.conversation.direction,
                                id: wsMessage.payload.conversation.messageId,
                                isAsset: wsMessage.payload.conversation.type === "ASSET" ? 1 : 0,
                                // locationId: // @todo
                                message: wsMessage.payload.conversation.message,
                                time: wsMessage.payload.conversation.time,
                                type: wsMessage.payload.conversation.type.toLowerCase() || "text",
                            },
                        },
                        lastInboundMessageTime: wsMessage.payload.conversation.time,
                        //firstMessageTimeLastInboundEntryGroup: wsMessage.payload.conversation.time,
                        platform: wsMessage.payload.conversation.platform,
                        read: false,
                        tagIds: [], // @todo
                        unreadMessages: 1,
                        statusFilter: resolveChatStatusFilter(
                            {
                                assignedUserId: wsMessage.payload.conversation.assignedUser,
                                assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                                lastConversation: {
                                    status:
                                        wsMessage.payload.conversation.conversationStatus ??
                                        wsMessage.payload.conversation.status,
                                },
                            },
                            teamsOfUser
                        ),
                    });

                    break;
                case "conversationUpdate":
                    if (
                        this.formattedActiveChat?.lastConversation.id === wsMessage.payload.conversationId &&
                        wsMessage.payload.payload.assignedUser !== getUserData().id
                    ) {
                        closeActiveChat(wsMessage.payload.conversationId);

                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("entities.conversation.Assign"),
                                text: i18n.t("entities.conversation.changed"),
                                icon: "exclamation-triangle",
                                variant: "warning",
                            },
                        });
                    }

                    updateChatInChatsList(wsMessage.payload.conversationId, {
                        assignedTeamId: wsMessage.payload.payload.assignedTeam,
                        assignedUserId: wsMessage.payload.payload.assignedUser,
                        status: wsMessage.payload.payload.status,
                        statusFilter: resolveChatStatusFilter(
                            {
                                assignedUserId: wsMessage.payload.payload.assignedUser,
                                assignedTeamId: wsMessage.payload.payload.assignedTeam,
                                lastConversation: {
                                    status: wsMessage.payload.payload.status,
                                },
                            },
                            teamsOfUser
                        ),
                    });

                    if (multiSelectEnabled.value) {
                        clearMultiSelect([{ id: wsMessage.payload.conversationId }]);
                    }

                    break;
                case "userUpdated":
                    updateUserStatus(parseInt(wsMessage.payload.id), wsMessage.payload.updates.status);
                    break;
                case "conversationReadForAll":
                    updateChatInChatsList(Number(wsMessage.payload.conversationId), {
                        read: true,
                    });
                    break;
                case "conversationUnreadForAll":
                    updateChatInChatsList(Number(wsMessage.payload.conversationId), {
                        read: false,
                    });
                    break;
            }
        },
    },
};
</script>

<style lang="scss">
@import "~@/assets/scss/components/inbox/inbox.scss";
</style>
